import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import "@/styles/luxury.css";
import "@/styles/phase2.css";
import "@/styles/phase3.css";
import "@/styles/phase5.css";
import "@/styles/phase6.css";
import "@/styles/phase7.css";
import "@/styles/phase8.css";
import "@/styles/phase9.css";
import "@/styles/phase10.css";
import "@/styles/phase12.css";
import "@/styles/phase13.css";
import "@/styles/phase17.css";
import "@/styles/phase18.css";
import "@/styles/phase21.css";
import "@/styles/phase28.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useSearchParams } from "react-router-dom";
import UnifiedAuthPage from "./pages/UnifiedAuthPage";
import DashboardHub from "./pages/DashboardHub";
import TraderDashboard from "./pages/TraderDashboard";

function LegacyAuthRedirect({ type, next }) {
  const [p] = useSearchParams();
  const q = new URLSearchParams({ type, next: p.get("next") || next });
  if (p.get("mode")) q.set("mode", p.get("mode"));
  return <Navigate to={`/auth?${q.toString()}`} replace />;
}
import { HelmetProvider } from "react-helmet-async";
import { AppProvider } from "./context/AppContext";
import { AuthProvider } from "./context/AuthContext";
import Navbar from "./components/Navbar";
import SiteFooter from "./components/SiteFooter";
import BrandToaster from "./components/BrandToaster";
import RouteMeta from "./components/RouteMeta";
import PageBackdrop from "./components/PageBackdrop";
import MobileTabBar from "./components/MobileTabBar";
import "@/styles/phase30.css";
import CollectionPage from "./pages/CollectionPage";
import CultivarDirectoryPage from "./pages/CultivarDirectoryPage";
import ContentDetailPage from "./pages/ContentDetailPage";
import LandingPage from "./pages/LandingPage";
import NotFoundPage from "./pages/NotFoundPage";
import AuthCallback from "./pages/AuthCallback";
import { initLenis, destroyLenis, syncLenisRoute } from "./lib/lenis";

const MapPage = lazy(() => import("./pages/MapPage"));
const KnowledgePage = lazy(() => import("./pages/KnowledgePage"));
const ProvenancePage = lazy(() => import("./pages/ProvenancePage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const VerifyPage = lazy(() => import("./pages/VerifyPage"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const StoriesPage = lazy(() => import("./pages/StoriesPage"));
const GalleryPage = lazy(() => import("./pages/GalleryPage"));
const CulinaryPage = lazy(() => import("./pages/CulinaryPage"));
const CulinaryDetailPage = lazy(() => import("./pages/CulinaryDetailPage"));
const StoryPage = lazy(() => import("./pages/StoryPage"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const SouqPage = lazy(() => import("./pages/SouqPage"));
const SouqListingPage = lazy(() => import("./pages/SouqListingPage"));
const BuyerDashboard = lazy(() => import("./pages/BuyerDashboard"));
const PaymentCallbackPage = lazy(() => import("./pages/PaymentCallbackPage"));
const RegionsPage = lazy(() => import("./pages/RegionsPage"));
const RegionPage = lazy(() => import("./pages/RegionPage"));

const RouteLoader = () => (
  <div className="route-loader" data-testid="route-loader" aria-hidden="true" />
);

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => { syncLenisRoute(pathname, hash); }, [pathname, hash]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  // Emergent Google Auth returns to {origin}/auth/callback#session_id=... — process it before anything else
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Suspense fallback={<RouteLoader />}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/map" element={<MapPage />} />
        <Route path="/knowledge" element={<KnowledgePage />} />
        <Route path="/provenance" element={<ProvenancePage />} />
        <Route path="/auth" element={<UnifiedAuthPage />} />
        <Route path="/dashboard" element={<DashboardHub />} />
        <Route path="/trader/dashboard" element={<TraderDashboard />} />
        <Route path="/provenance/auth" element={<LegacyAuthRedirect type="farmer" next="/provenance/dashboard" />} />
        <Route path="/provenance/dashboard" element={<DashboardPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/verify/:certId" element={<VerifyPage />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/admin/:section" element={<AdminPage />} />
        <Route path="/varieties" element={<CollectionPage kind="varieties" />} />
        <Route path="/varieties/directory" element={<CultivarDirectoryPage />} />
        <Route path="/varieties/:slug" element={<ContentDetailPage kind="varieties" />} />
        <Route path="/oases" element={<CollectionPage kind="oases" />} />
        <Route path="/oases/:slug" element={<ContentDetailPage kind="oases" />} />
        <Route path="/routes" element={<CollectionPage kind="routes" />} />
        <Route path="/routes/:slug" element={<ContentDetailPage kind="routes" />} />
        <Route path="/gallery" element={<GalleryPage />} />
        <Route path="/culinary" element={<CulinaryPage />} />
        <Route path="/culinary/:slug" element={<CulinaryDetailPage />} />
        <Route path="/stories" element={<StoriesPage />} />
        <Route path="/stories/:slug" element={<StoryPage />} />
        <Route path="/terms" element={<LegalPage doc="terms" />} />
        <Route path="/privacy" element={<LegalPage doc="privacy" />} />
        <Route path="/dmca" element={<LegalPage doc="dmca" />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/souq" element={<SouqPage />} />
        <Route path="/souq/auth" element={<LegacyAuthRedirect type="buyer" next="/souq/dashboard" />} />
        <Route path="/souq/dashboard" element={<BuyerDashboard />} />
        <Route path="/souq/payment/callback" element={<PaymentCallbackPage />} />
        <Route path="/regions" element={<RegionsPage />} />
        <Route path="/regions/:slug" element={<RegionPage />} />
        <Route path="/souq/listing/:listingId" element={<SouqListingPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  useEffect(() => { initLenis(); return destroyLenis; }, []);

  return (
    <HelmetProvider>
      <AppProvider>
        <AuthProvider>
          <BrowserRouter>
            <ScrollManager />
            <RouteMeta />
            <PageBackdrop />
            <BrandToaster />
            <Navbar />
            <AppRouter />
            <SiteFooter />
            <MobileTabBar />
          </BrowserRouter>
        </AuthProvider>
      </AppProvider>
    </HelmetProvider>
  );
}

export default App;
